import { createContext, useContext, useLayoutEffect, useRef, useState, type RefObject } from 'react';

/**
 * Livello di dettaglio del rilievo 3D, scelto dal diametro REALE in px del pezzo.
 *
 * Perché misurato e non una media query: la scacchiera è il quadrato più grande che
 * entra in `.gs-middle`, cioè `min(100cqw, 100cqh)` della banda che avanza dopo
 * intestazione e barre (GameScreen.css). Quell'altezza residua dipende
 * dal viewport, sì, ma anche da quanto è alta l'intestazione in QUESTA lingua e da
 * quali barre sono in scena: due dispositivi con la stessa larghezza di viewport
 * hanno pezzi di dimensioni diverse, e nessuna soglia scritta a mano lo prevede.
 * Inoltre replay, puzzle ed editor hanno contenitori propri che nessuna regola
 * `data-board-size` intercetta.
 *
 * Diametri misurati a viewport 360px (svg 322px, pezzo = 0.8 · casella):
 *   8x8 32.2px · 10x10 25.8px · 12x12 21.5px · 14x14 (South African) 18.4px
 */
export type PieceLod = 'full' | 'mid' | 'low' | 'micro';

/** I tier dal più dettagliato al più povero. */
export const LOD_TIERS = ['full', 'mid', 'low', 'micro'] as const satisfies readonly PieceLod[];

export interface LodSpec {
  /** Quanti solchi concentrici disegnare (i primi N di `StyleGeometry.rings`). */
  rings: number;
  /**
   * Se disegnare il bottone tornito al centro della faccia.
   *
   * Quel che rende visibile un bottone è l'OMBRA alla sua base, non il piano più
   * chiaro: su una pedina chiara una velatura schiarente sparisce, mentre lo scalino
   * scuro si vede fino a 18px (verificato rasterizzando alle dimensioni vere, non
   * dedotto). Sotto, però, lo scalino è l'unica cosa che resterebbe — e a quel punto
   * la pedina porterebbe un dischetto scuro al centro proprio dove la dama porta il
   * suo emblema collassato: due pezzi diversi con lo stesso segno.
   */
  boss: 'step' | 'none';
  /**
   * Spessore dei solchi, frazione di `radius` (NON del diametro).
   *
   * Risolto sulla dimensione MINIMA di ciascun tier, così ogni linea supera 1px
   * reale anche nel caso peggiore — larghezza px = strokeK · diametro/2:
   *   full  0.075 @ 30px => 1.13px
   *   mid   0.105 @ 22px => 1.16px
   *   low   0.140 @ 16px => 1.12px
   */
  strokeK: number;
  /** Giuntura tra i due dischi della dama impilata. */
  seam: boolean;
  /**
   * Overlay di venatura del materiale.
   *
   * Le fibre del pattern sono spesse una frazione del pezzo: sotto i 22px di diametro
   * finiscono sotto il pixel e si trasformano in una velatura sporca uniforme, che
   * toglie contrasto senza aggiungere informazione. Meglio il materiale liscio.
   */
  grain: boolean;
  /**
   * Riflesso del pezzo sulla lastra di VETRO.
   *
   * Il riflesso è alto poco più di mezzo pezzo e sfuma a zero: sotto i 22px di diametro
   * sono sei pixel schiacciati sotto una sfumatura, cioè niente che si veda — e ognuno
   * costa comunque una maschera e un buffer fuori schermo. Stessa soglia della venatura,
   * e per la stessa ragione: sotto, il dettaglio non arriva ai pixel.
   *
   * Conseguenza, sui diametri tabulati qui sopra a 360px di viewport: i riflessi
   * esistono su 8x8 (32.2px) e 10x10 (25.8px) e si spengono da soli su 12x12 (21.5px) e
   * 14x14 (18.4px) — cioè proprio dove i pezzi sono di più.
   */
  reflection: boolean;
  /**
   * Quanto della corona 3D si disegna. Non è una scala: è la corona che PERDE PEZZI, e
   * l'ordine in cui li perde è quello che li rende inutili sotto una certa dimensione.
   *
   * Il TIPO di corona (`crownType`) non entra qui: quello è la forma, e si vede a ogni tier
   * tranne l'ultimo. Qui si perdono solo le rifiniture.
   *
   * - `rich` — tutto: punte dietro, parete interna, incastonature, riflesso sulla fascia,
   *   luce sulle sferette, cuscinetto e crocetta dell'imperiale.
   * - `plain` — via le rifiniture piccole (una pietra ha raggio ~0.35px sotto i 26px di
   *   pezzo: velatura, non gemma), restano volume e sagoma.
   * - `front` — via anche le punte posteriori e la parete interna. Sono i tre-quattro pixel
   *   dietro l'apertura: a quelle dimensioni si fondono con le punte davanti e la corona
   *   diventa una massa unica invece di un anello.
   * - `solid` — una silhouette piena e basta: niente sferette, niente archi, e il giglio
   *   torna una punta liscia. Sotto i 16px non c'è tipo che si distingua, come non c'è
   *   incisione che si legga.
   */
  crown: 'rich' | 'plain' | 'front' | 'solid';
}

export const LOD_SPEC: Record<PieceLod, LodSpec> = {
  full: { rings: 2, boss: 'step', strokeK: 0.075, seam: true, grain: true, reflection: true, crown: 'rich' },
  mid: { rings: 1, boss: 'step', strokeK: 0.105, seam: true, grain: true, reflection: true, crown: 'plain' },
  low: { rings: 1, boss: 'step', strokeK: 0.14, seam: true, grain: false, reflection: false, crown: 'front' },
  // Sotto i 16px l'incisione se ne va tutta: i solchi diventerebbero fango, e il
  // bottone resterebbe un dischetto scuro al centro della PEDINA proprio dove la dama
  // porta il suo emblema collassato — due pezzi diversi con lo stesso segno. Restano
  // parete e giuntura, che sono riempimenti senza soglia sub-pixel: lo stile 3D non
  // sparisce mai, l'incisione sì. A quella dimensione il pezzo è un gettone.
  micro: { rings: 0, boss: 'none', strokeK: 0.14, seam: true, grain: false, reflection: false, crown: 'solid' },
};

const FULL_MIN_PX = 30;
const MID_MIN_PX = 22;
const LOW_MIN_PX = 16;

/**
 * Diametro MINIMO in px a cui ciascun tier viene scelto: è il caso peggiore del tier,
 * quindi è là che vanno verificate le soglie di leggibilità dell'incisione.
 *
 * `micro` non ha un minimo vero — sotto ci si arriva sempre — ma disegna solo aree
 * piene, che non hanno soglia sub-pixel.
 */
export const TIER_MIN_DIAMETER_PX: Record<PieceLod, number> = {
  full: FULL_MIN_PX,
  mid: MID_MIN_PX,
  low: LOW_MIN_PX,
  micro: 0,
};

/** Tier corrispondente a un diametro renderizzato in px. */
export function reliefLod(diameterPx: number): PieceLod {
  if (diameterPx >= FULL_MIN_PX) return 'full';
  if (diameterPx >= MID_MIN_PX) return 'mid';
  if (diameterPx >= LOW_MIN_PX) return 'low';
  return 'micro';
}

/**
 * Tier da mostrare nell'ANTEPRIMA delle impostazioni per una scacchiera di lato `boardSize`.
 *
 * L'anteprima disegna il pezzo molto più grande del vero (1 unità = 1px in un riquadro
 * da 112px), quindi senza questo l'utente vedrebbe tre solchi e ne troverebbe uno solo
 * in partita. Deliberatamente restituisce il tier del caso PEGGIORE — quello di un
 * telefono da 360px, dalla tabella misurata sopra — perché l'anteprima serve a non far
 * scegliere uno stile che su mobile diventa fango.
 */
export function previewLod(boardSize: number): PieceLod {
  if (boardSize <= 8) return 'full';
  if (boardSize <= 10) return 'mid';
  return 'low';
}

/**
 * Il tier corrente della scacchiera che ospita il pezzo. `null` = nessun host ha
 * misurato: il `Piece` ricade su `reliefLod(size)`, che è la scelta giusta per gli
 * usi autonomi in cui 1 unità SVG ≈ 1px (icone catture, swatch, test).
 */
export const PieceLodContext = createContext<PieceLod | null>(null);

export function usePieceLod(): PieceLod | null {
  return useContext(PieceLodContext);
}

/**
 * Isteresi: senza dead band un pezzo da 21.9px oscillerebbe tra `low` e `mid` a ogni
 * rotazione o ridimensionamento, cambiando aspetto sotto le mani del giocatore —
 * e ogni cambio ridisegna tutti i pezzi (React.memo è già neutralizzato da cloneBoard).
 */
const HYSTERESIS_PX = 2;

function lodWithHysteresis(diameterPx: number, current: PieceLod | null): PieceLod {
  const next = reliefLod(diameterPx);
  if (current === null || next === current) return next;
  // Si cambia tier solo superando la soglia di almeno HYSTERESIS_PX.
  const thresholds: Record<Exclude<PieceLod, 'micro'>, number> = {
    full: FULL_MIN_PX,
    mid: MID_MIN_PX,
    low: LOW_MIN_PX,
  };
  const order: PieceLod[] = ['micro', 'low', 'mid', 'full'];
  const goingUp = order.indexOf(next) > order.indexOf(current);
  const boundary = goingUp ? thresholds[next as Exclude<PieceLod, 'micro'>] : thresholds[current as Exclude<PieceLod, 'micro'>];
  if (boundary === undefined) return next;
  return Math.abs(diameterPx - boundary) < HYSTERESIS_PX ? current : next;
}

/**
 * Misura l'`<svg>` della scacchiera e ne ricava il tier LOD dei pezzi.
 *
 * `useLayoutEffect` + `getBoundingClientRect()` fanno sì che il PRIMO frame dipinto sia
 * già corretto: misurare dentro `useEffect` farebbe lampeggiare 70 pezzi a pieno
 * dettaglio su una scacchiera 14x14 mobile, cioè esattamente il fotogramma che il
 * mandato mobile-first vieta.
 *
 * @param svgRef        l'elemento <svg> della scacchiera
 * @param viewBoxUnits  larghezza del viewBox in unità SVG
 * @param pieceUnits    diametro del pezzo nelle stesse unità
 */
export function useBoardLod(
  svgRef: RefObject<SVGSVGElement | null>,
  viewBoxUnits: number,
  pieceUnits: number,
): PieceLod | null {
  const [lod, setLod] = useState<PieceLod | null>(null);
  const lodRef = useRef<PieceLod | null>(null);

  useLayoutEffect(() => {
    const el = svgRef.current;
    if (!el || viewBoxUnits <= 0) return;

    const measure = () => {
      const widthPx = el.getBoundingClientRect().width;
      // jsdom (e un <svg> non ancora in layout) restituisce 0: meglio nessuna
      // misura che una misura falsa, così si usa il fallback su `size`.
      if (widthPx <= 0) return;
      const diameterPx = (pieceUnits / viewBoxUnits) * widthPx;
      const next = lodWithHysteresis(diameterPx, lodRef.current);
      if (next !== lodRef.current) {
        lodRef.current = next;
        setLod(next);
      }
    };

    measure();

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [svgRef, viewBoxUnits, pieceUnits]);

  return lod;
}
